/** 程序化 Web Audio — 事件驱动 */
export class AudioSystem {
  static muted = false;
  private context: AudioContext | null = null;
  private unlocked = false;

  constructor() {
    const unlock = () => {
      void this.unlock();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    this.context = new Ctx();
    await this.context.resume();
    this.unlocked = true;
  }

  private tone(
    freq: number,
    dur: number,
    type: OscillatorType = 'triangle',
    vol = 0.08,
    slideTo?: number,
  ): void {
    if (AudioSystem.muted || !this.context || this.context.state !== 'running') return;
    const now = this.context.currentTime;
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, now + dur * 0.85);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(vol, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(gain).connect(this.context.destination);
    osc.start(now);
    osc.stop(now + dur + 0.05);
  }

  fire(): void {
    this.tone(220, 0.12, 'square', 0.05, 180);
    this.tone(90, 0.18, 'sawtooth', 0.03);
  }

  grab(kind: string): void {
    if (kind === 'rock' || kind === 'trash') {
      this.tone(120, 0.2, 'square', 0.06, 60);
    } else if (kind === 'dynamite') {
      this.tone(80, 0.25, 'sawtooth', 0.1, 40);
    } else {
      this.tone(520, 0.18, 'triangle', 0.07, 880);
    }
  }

  deposit(gain: number): void {
    const base = 400 + Math.min(600, gain * 0.15);
    this.tone(base, 0.14, 'triangle', 0.08, base * 1.5);
    setTimeout(() => this.tone(base * 1.25, 0.12, 'triangle', 0.06), 70);
  }

  blast(): void {
    this.tone(70, 0.35, 'sawtooth', 0.12, 35);
    this.tone(200, 0.2, 'square', 0.05, 50);
  }

  chip(): void {
    this.tone(660, 0.1, 'triangle', 0.07, 990);
    setTimeout(() => this.tone(990, 0.12, 'triangle', 0.06), 60);
  }

  event(type: string): void {
    if (type === 'blackout') this.tone(180, 0.4, 'sine', 0.06, 60);
    else if (type === 'goldRain') this.tone(700, 0.2, 'triangle', 0.07, 1100);
    else if (type === 'caveIn') this.tone(90, 0.35, 'sawtooth', 0.09, 40);
    else this.tone(440, 0.15, 'triangle', 0.05);
  }

  win(): void {
    [523, 659, 784, 1046].forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.22, 'triangle', 0.08), i * 100);
    });
  }

  fail(): void {
    this.tone(220, 0.25, 'sawtooth', 0.08, 110);
    setTimeout(() => this.tone(110, 0.4, 'sawtooth', 0.07, 55), 150);
  }

  ui(): void {
    this.tone(620, 0.06, 'triangle', 0.04);
  }

  dispose(): void {
    void this.context?.close();
    this.context = null;
    this.unlocked = false;
  }
}
